import React, { useState, useCallback } from 'react';
import { 
  IonModal, 
  IonHeader, 
  IonToolbar, 
  IonTitle, 
  IonContent, 
  IonButton, 
  IonButtons,
  IonIcon,
  IonItem,
  IonLabel,
  IonRange,
  IonText,
  IonSpinner,
  IonGrid,
  IonRow,
  IonCol
} from '@ionic/react';
import { checkmark, close, refresh } from 'ionicons/icons';
import Cropper from 'react-easy-crop';
import { cropImage, loadImage, formatFileSize } from '../utils/imageOptimization';
import './ImageCropEditor.css';

interface ImageCropEditorProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  imageFile?: File;
  imageUrl?: string;
  onImageCropped: (croppedImageBlob: Blob, croppedImageUrl: string) => void;
  targetSize?: { width: number; height: number };
  title?: string;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Componente para recortar y optimizar imágenes antes de subirlas
 * Permite al usuario seleccionar el área de recorte y genera una imagen de 500x500px
 */
const ImageCropEditor: React.FC<ImageCropEditorProps> = ({
  isOpen,
  onDidDismiss,
  imageFile,
  imageUrl,
  onImageCropped,
  targetSize = { width: 500, height: 500 },
  title = 'Recortar imagen de perfil'
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [processing, setProcessing] = useState(false);
  const [imageToEdit, setImageToEdit] = useState<string>('');
  const [originalImage, setOriginalImage] = useState<HTMLImageElement | null>(null);
  const [imageInfo, setImageInfo] = useState<{
    originalSize: number;
    dimensions: string;
  } | null>(null);

  // Cargar imagen cuando se abre el modal
  React.useEffect(() => {
    if (isOpen && (imageFile || imageUrl)) {
      loadImageForEditing();
    }
  }, [isOpen, imageFile, imageUrl]);

  const loadImageForEditing = async () => {
    try {
      setProcessing(true);
      
      let imgSrc: string;
      let fileSize = 0;
      
      if (imageFile) {
        imgSrc = URL.createObjectURL(imageFile);
        fileSize = imageFile.size;
      } else if (imageUrl) {
        imgSrc = imageUrl;
      } else {
        return;
      }

      setImageToEdit(imgSrc);
      
      // Cargar información de la imagen
      const img = await loadImage(imgSrc);
      setOriginalImage(img);
      setImageInfo({
        originalSize: fileSize,
        dimensions: `${img.naturalWidth} × ${img.naturalHeight}px`
      });
      
    } catch (error) {
      console.error('Error loading image:', error);
    } finally {
      setProcessing(false);
    }
  };

  const onCropComplete = useCallback(
    (croppedArea: any, croppedAreaPixels: CropArea) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const handleCropImage = async () => {
    if (!originalImage || !croppedAreaPixels) {
      return;
    }

    setProcessing(true);
    
    try {
      const result = await cropImage(
        originalImage,
        croppedAreaPixels,
        targetSize.width,
        targetSize.height
      );

      if (result.success && result.blob && result.dataUrl) {
        onImageCropped(result.blob, result.dataUrl);
        onDidDismiss();
      } else {
        console.error('Error cropping image:', result.error);
      }
    } catch (error) {
      console.error('Error processing crop:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleClose = () => {
    // Limpiar URL del objeto si se creó
    if (imageFile && imageToEdit) {
      URL.revokeObjectURL(imageToEdit);
    }
    setImageToEdit('');
    setOriginalImage(null);
    setImageInfo(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onDidDismiss();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{title}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose} disabled={processing}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {processing && !imageToEdit ? (
          <div className="crop-editor-loading">
            <IonSpinner name="crescent" />
            <IonText>
              <p>Cargando imagen...</p>
            </IonText>
          </div>
        ) : (
          <>
            {/* Información de la imagen */}
            {imageInfo && (
              <IonItem lines="none" className="image-info">
                <IonLabel>
                  <h3>Información de la imagen</h3>
                  <p>Dimensiones: {imageInfo.dimensions}</p>
                  {imageInfo.originalSize > 0 && (
                    <p>Tamaño: {formatFileSize(imageInfo.originalSize)}</p>
                  )}
                  <p>Resultado: {targetSize.width} × {targetSize.height}px</p>
                </IonLabel>
              </IonItem>
            )}

            {/* Área de recorte */}
            {imageToEdit && (
              <div className="crop-container">
                <Cropper
                  image={imageToEdit}
                  crop={crop}
                  zoom={zoom}
                  aspect={1} // Aspecto cuadrado para foto de perfil
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  showGrid={true}
                  cropShape="round" // Forma circular para foto de perfil
                />
              </div>
            )}

            {/* Controles */}
            <div className="crop-controls">
              <IonGrid>
                <IonRow>
                  <IonCol>
                    <IonItem lines="none">
                      <IonLabel position="stacked">Zoom</IonLabel>
                      <IonRange
                        min={1}
                        max={3}
                        step={0.1}
                        value={zoom}
                        onIonChange={(e) => setZoom(e.detail.value as number)}
                        color="primary"
                        disabled={processing}
                      />
                    </IonItem>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </div>

            {/* Botones de acción */}
            <div className="crop-actions">
              <IonGrid>
                <IonRow>
                  <IonCol size="6">
                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={handleReset}
                      disabled={processing}
                    >
                      <IonIcon icon={refresh} slot="start" />
                      Reiniciar
                    </IonButton>
                  </IonCol>
                  <IonCol size="6">
                    <IonButton
                      expand="block"
                      onClick={handleCropImage}
                      disabled={processing || !croppedAreaPixels}
                    >
                      {processing ? (
                        <IonSpinner name="crescent" />
                      ) : (
                        <>
                          <IonIcon icon={checkmark} slot="start" />
                          Aplicar
                        </>
                      )}
                    </IonButton>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </div>

            {/* Instrucciones */}
            <IonItem lines="none" className="crop-instructions">
              <IonLabel>
                <IonText color="medium">
                  <p>
                    • Arrastra para mover el área de recorte<br/>
                    • Usa el zoom para ajustar el tamaño<br/>
                    • La imagen final será de {targetSize.width}×{targetSize.height} píxeles
                  </p>
                </IonText>
              </IonLabel>
            </IonItem>
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

export default ImageCropEditor;
